import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["taxCode", "vatNumber", "sdiCode", "taxCodeError", "vatNumberError", "sdiCodeError", "vatNumberWrapper", "memberType", "companyName"]
  static values = { memberId: String, checkUrl: String }

  connect() {
    // Auto-uppercase on input
    if (this.hasCompanyNameTarget) {
      this.companyNameTarget.addEventListener("input", () => {
        this.companyNameTarget.value = this.companyNameTarget.value.toUpperCase()
      })
    }
    if (this.hasTaxCodeTarget) {
      this.taxCodeTarget.addEventListener("input", () => {
        this.taxCodeTarget.value = this.taxCodeTarget.value.toUpperCase()
        this.checkTaxCodeUniqueness()
      })
    }
    if (this.hasVatNumberTarget) {
      this.vatNumberTarget.addEventListener("input", () => {
        this.vatNumberTarget.value = this.vatNumberTarget.value.replace(/\D/g, "")
        this.checkVatNumberUniqueness()
      })
    }
    if (this.hasSdiCodeTarget) {
      this.sdiCodeTarget.addEventListener("input", () => {
        this.sdiCodeTarget.value = this.sdiCodeTarget.value.toUpperCase()
      })
    }
  }

  validateTaxCode() {
    if (!this.hasTaxCodeTarget) return

    const value = this.taxCodeTarget.value.trim().toUpperCase()
    const errorElement = this.hasTaxCodeErrorTarget ? this.taxCodeErrorTarget : null

    if (!value) {
      this.setError(this.taxCodeTarget, errorElement, "Il codice fiscale è obbligatorio")
      return
    }

    // Check if it's a VAT number format (11 digits)
    if (/^\d{11}$/.test(value)) {
      // Verifica solo il codice provincia (cifre 8-10)
      if (this.isValidProvinceCode(value)) {
        this.clearError(this.taxCodeTarget, errorElement)
      } else {
        this.setError(this.taxCodeTarget, errorElement, "Codice provincia non valido")
      }
      return
    }

    // Check if it's a fiscal code format (16 alphanumeric)
    if (/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(value)) {
      this.clearError(this.taxCodeTarget, errorElement)
      return
    }

    this.setError(this.taxCodeTarget, errorElement, "Formato non valido: 16 caratteri (CF) o 11 cifre (P.IVA)")
  }

  validateVatNumber() {
    if (!this.hasVatNumberTarget) return

    const value = this.vatNumberTarget.value.trim()
    const errorElement = this.hasVatNumberErrorTarget ? this.vatNumberErrorTarget : null

    if (!value) {
      this.clearError(this.vatNumberTarget, errorElement)
      return
    }

    if (!/^\d{11}$/.test(value)) {
      this.setError(this.vatNumberTarget, errorElement, "La Partita IVA deve essere di 11 cifre")
      return
    }

    // Verifica solo il codice provincia (cifre 8-10)
    if (this.isValidProvinceCode(value)) {
      this.clearError(this.vatNumberTarget, errorElement)
    } else {
      this.setError(this.vatNumberTarget, errorElement, "Codice provincia non valido")
    }
  }

  validateSdiCode() {
    if (!this.hasSdiCodeTarget) return

    const value = this.sdiCodeTarget.value.trim().toUpperCase()
    const errorElement = this.hasSdiCodeErrorTarget ? this.sdiCodeErrorTarget : null

    if (!value) {
      this.clearError(this.sdiCodeTarget, errorElement)
      return
    }

    // Codici SDI speciali validi:
    // - "0000000": soggetto non obbligato a fatturazione elettronica
    // - "XXXXXXX": soggetto estero (non residente)
    if (value === "0000000" || value === "XXXXXXX") {
      this.clearError(this.sdiCodeTarget, errorElement)
      return
    }

    if (/^[A-Z0-9]{6,7}$/.test(value)) {
      this.clearError(this.sdiCodeTarget, errorElement)
    } else {
      this.setError(this.sdiCodeTarget, errorElement, "Il codice SDI deve essere di 6 o 7 caratteri alfanumerici")
    }
  }

  toggleVatNumber(event) {
    const memberType = event.target.value
    if (this.hasVatNumberWrapperTarget) {
      this.vatNumberWrapperTarget.classList.toggle('hidden', memberType === 'individual')
    }
  }

  checkTaxCodeUniqueness() {
    if (!this.hasTaxCodeTarget) return
    const value = this.taxCodeTarget.value.trim().toUpperCase()

    // Controlla solo quando il campo ha la lunghezza giusta (11 o 16)
    if (value.length !== 11 && value.length !== 16) {
      this.removeUniquenessLabel(this.taxCodeTarget)
      return
    }

    this.checkUniqueness("tax_code", value, this.taxCodeTarget)
  }

  checkVatNumberUniqueness() {
    if (!this.hasVatNumberTarget) return
    const value = this.vatNumberTarget.value.trim()

    if (value.length !== 11) {
      this.removeUniquenessLabel(this.vatNumberTarget)
      return
    }

    this.checkUniqueness("vat_number", value, this.vatNumberTarget)
  }

  async checkUniqueness(field, value, inputElement) {
    const url = new URL(this.checkUrlValue || "/members/check_uniqueness", window.location.origin)
    url.searchParams.set("field", field)
    url.searchParams.set("value", value)
    if (this.memberIdValue) {
      url.searchParams.set("member_id", this.memberIdValue)
    }

    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      this.showUniquenessLabel(inputElement, data)
    } catch {
      this.removeUniquenessLabel(inputElement)
    }
  }

  showUniquenessLabel(inputElement, data) {
    this.removeUniquenessLabel(inputElement)

    const label = document.createElement("p")
    label.classList.add("mt-1", "text-sm", "font-medium", "uniqueness-label")

    if (data.exists) {
      label.classList.add("text-red-600")
      label.textContent = `Già presente: ${data.company_name} (${data.member_code})`
      inputElement.classList.add("border-red-500")
      inputElement.classList.remove("border-green-500", "border-gray-300", "dark:border-gray-700")
    } else {
      label.classList.add("text-green-600")
      label.textContent = "Disponibile"
      inputElement.classList.add("border-green-500")
      inputElement.classList.remove("border-red-500", "border-gray-300", "dark:border-gray-700")
    }

    inputElement.parentElement.appendChild(label)
  }

  removeUniquenessLabel(inputElement) {
    const existing = inputElement.parentElement.querySelector(".uniqueness-label")
    if (existing) existing.remove()
    inputElement.classList.remove("border-green-500", "border-red-500")
    inputElement.classList.add("border-gray-300", "dark:border-gray-700")
  }

  isValidProvinceCode(vat) {
    // Codice provincia: cifre 8-10 (indici 7-9 in 0-indexed)
    // Valori validi: 001-100 (province), 120-121 (sedi secondarie), 888 (intracomunitari), 999 (test)
    const provinceCode = parseInt(vat.substring(7, 10), 10)
    return (provinceCode >= 1 && provinceCode <= 100) ||
           (provinceCode >= 120 && provinceCode <= 121) ||
           provinceCode === 888 ||
           provinceCode === 999
  }

  setError(input, errorElement, message) {
    input.classList.add("border-red-500", "focus:border-red-500", "focus:ring-red-500")
    input.classList.remove("border-gray-300", "dark:border-gray-700")

    if (errorElement) {
      errorElement.textContent = message
      errorElement.classList.remove("hidden")
    }
  }

  clearError(input, errorElement) {
    input.classList.remove("border-red-500", "focus:border-red-500", "focus:ring-red-500")
    input.classList.add("border-gray-300", "dark:border-gray-700")

    if (errorElement) {
      errorElement.textContent = ""
      errorElement.classList.add("hidden")
    }
  }
}
